import { NetworkResponse } from 'data-layer-cr/dist/network/NetworkTypes';
import { DisplayItem, FormInputProps, FormValueTypes } from 'design-system';
import { Dispatch, SetStateAction } from 'react';
import COSTA_RICA from 'src/constants/countryCode';
import { moneyTypes } from 'src/constants/money';
import { cities } from 'src/data/citiesCr.json';
import { civilStatus } from 'src/data/civilStatus.json';
import { states } from 'src/data/statesCr.json';
import { townships } from 'src/data/townshipsCr.json';

import EmissionDataFields from 'src/models/helperTypes/EmissionDataFields';
import { PersonInnova } from 'src/models/helperTypes/InnovaDataFields';
import PaymentIssuer from 'src/models/helperTypes/PaymentIssuer';
import { NaturalPersonEquifaxResponse } from 'src/models/responseTypes/NaturalPersonEquifaxResponse';

interface AddNaturalPersonEquifaxProps {
  dataFields: EmissionDataFields;
  person: NetworkResponse<NaturalPersonEquifaxResponse>;
  getInputProps: (inputName: string) => FormInputProps;
  setNewDataFieldsAction: (dataFields: Partial<EmissionDataFields>) => void;
  setCityOptions: Dispatch<SetStateAction<DisplayItem[]>>;
  setTownshipOptions: Dispatch<SetStateAction<DisplayItem[]>>;
  updateMultipleInputs: (newInputData: {
    [key: string]: FormValueTypes;
  }) => void;
}
interface AddNaturalPersonAcselProps {
  dataFields: EmissionDataFields;
  person?: PersonInnova | null;
  getInputProps: (inputName: string) => FormInputProps;
  setNewDataFieldsAction: (dataFields: Partial<EmissionDataFields>) => void;
  setCityOptions: Dispatch<SetStateAction<DisplayItem[]>>;
  setTownshipOptions: Dispatch<SetStateAction<DisplayItem[]>>;
  updateMultipleInputs: (newInputData: {
    [key: string]: FormValueTypes;
  }) => void;
}
interface AddLegalPersonAcselProps {
  dataFields: EmissionDataFields;
  legalPerson?: PersonInnova | null;
  getInputProps: (inputName: string) => FormInputProps;
  setNewDataFields: (dataFields: Partial<EmissionDataFields>) => void;
  setCityOptions: Dispatch<SetStateAction<DisplayItem[]>>;
  setTownshipOptions: Dispatch<SetStateAction<DisplayItem[]>>;
}
interface PayerPersonEquifaxProps {
  dataFields: PaymentIssuer;
  person: NetworkResponse<NaturalPersonEquifaxResponse>;
  getInputProps: (inputName: string) => FormInputProps;
  setNewPayerDataFieldsAction: (
    dataFields: Partial<EmissionDataFields>,
  ) => void;
}

interface AddLegalPersonEquifaxProps {
  person: NetworkResponse<NaturalPersonEquifaxResponse>;
  getInputProps: (inputName: string) => FormInputProps;
  prefixKey: string;
  setBeneficiaryCityOptions: Dispatch<SetStateAction<DisplayItem[]>>;
  setBeneficiaryTownshipOptions: Dispatch<SetStateAction<DisplayItem[]>>;
}

interface ShareholderEquifaxProps {
  index: number;
  person: NetworkResponse<NaturalPersonEquifaxResponse>;
  getInputProps: (inputName: string) => FormInputProps;
}

export const getEquifaxDate = (dateEquifax: string): string => {
  const year = dateEquifax.substring(0, 4);
  const month = dateEquifax.substring(4, 6);
  const day = dateEquifax.substring(6, 8);
  return `${day}/${month}/${year}`;
};

export const getAcselDate = (dateAcsel: string): string => {
  const [year, month, day] = dateAcsel.split('-');
  return `${day}/${month}/${year}`;
};

export const getAcselSalary = (value: string): number => {
  return Number(value.replace(/,/g, '')) * 500;
};

export const getGenderEquifax = (gender: string): string =>
  gender === 'MASCULINO' ? 'M' : 'F';

export const getCatalog = (
  catalog: typeof states,
  code?: string,
  parentId?: number,
): (typeof states)[number] | undefined => {
  let newCatalog = catalog;
  if (parentId) {
    newCatalog = catalog.filter((item) => item.parentId === parentId);
  }
  return newCatalog.find(
    (item) => item.name.toLowerCase() === code?.toLowerCase(),
  );
};

export const getCatalogToOptions = (
  catalog: typeof states,
  code?: number,
): DisplayItem[] =>
  catalog
    .filter((item) => item.parentId === code)
    .map((item) => ({
      Value: item.code,
      Text: item.name,
    }));

export const getCivilStatusIdByCode = (code: string): string => {
  if (!code) return '';
  const newCode = code.split('/')[0].toLowerCase();
  return (
    civilStatus.find((item) => item.name.toLowerCase().includes(newCode))
      ?.code ?? ''
  );
};

export const addNaturalPersonEquifax = async ({
  person,
  dataFields,
  getInputProps,
  setNewDataFieldsAction,
  setCityOptions,
  setTownshipOptions,
  updateMultipleInputs,
}: AddNaturalPersonEquifaxProps): Promise<void> => {
  updateMultipleInputs({
    names: '',
    lastNames: '',
    birthday: '',
    gender: '',
    civilStatus: '',
    birthPlace: '',
    addressCountry: '',
    addressState: '',
    addressDetail: '',
    addressCity: '',
    addressTownship: '',
    originType: '',
    enterprise: '',
    originCountry: '',
    salary: '',
    typeOfMoney: moneyTypes[0].Value,
    phoneNumber: '',
    email: '',
    occupation: '',
  });
  if (!person.data.personas_estudio_completo) return;
  const {
    nombre,
    apellido1,
    apellido2,
    nacimiento,
    genero,
    provincia,
    canton,
    distrito_electoral: district,
    desc_distrito_administrativo: districtName,
  } = person.data.personas_estudio_completo.datos_filiacion;
  const { direcciones } = person.data.personas_estudio_completo;
  const { estado_civil: status } =
    person.data.personas_estudio_completo.datos_estado_civil;
  getInputProps('names').onChange(nombre || dataFields.names);
  const lastName = (
    apellido1 || apellido2 ? `${apellido1} ${apellido2}` : dataFields.lastNames
  ).trim();
  getInputProps('lastNames').onChange(lastName);
  const birthday = nacimiento
    ? getEquifaxDate(nacimiento)
    : dataFields.birthday;
  getInputProps('birthday').onChange(birthday);
  getInputProps('email').onChange(dataFields.email);
  getInputProps('typeOfMoney').onChange(dataFields.typeOfMoney);
  getInputProps('phoneNumber').onChange(dataFields.phoneNumber);
  const gender = genero ? getGenderEquifax(genero) : dataFields.gender;
  getInputProps('gender').onChange(gender);
  const civilStatusId = getCivilStatusIdByCode(status);
  getInputProps('civilStatus').onChange(civilStatusId);
  const state = getCatalog(states, provincia);
  const newState = state?.code ? `${COSTA_RICA}-${state.code}` : '';
  getInputProps('birthPlace').onChange(newState);
  getInputProps('addressCountry').onChange(COSTA_RICA);
  getInputProps('addressState').onChange(state?.code);
  const newAddress = [...direcciones];
  const address =
    newAddress.length > 0
      ? newAddress.sort(
          (a, b) =>
            new Date(getEquifaxDate(b.fecha)).getTime() -
            new Date(getEquifaxDate(a.fecha)).getTime(),
        )[0].direccion
      : '';
  getInputProps('addressDetail').onChange(address);
  setNewDataFieldsAction({
    names: nombre || dataFields.names,
    lastNames: lastName,
    birthday,
    gender,
  });
  setCityOptions(getCatalogToOptions(cities, state?.id));
  const city = getCatalog(
    cities,
    canton.toLowerCase() === 'central' ? provincia : canton,
    state?.id,
  );
  getInputProps('addressCity').onChange(city?.code);
  setTownshipOptions(getCatalogToOptions(townships, city?.id));

  const township =
    getCatalog(townships, district, city?.id)?.code ??
    getCatalog(townships, districtName, city?.id)?.code;
  getInputProps('addressTownship').onChange(township);
};

export const addNaturalPersonACSEL = async ({
  person,
  dataFields,
  getInputProps,
  setNewDataFieldsAction,
  setCityOptions,
  setTownshipOptions,
  updateMultipleInputs,
}: AddNaturalPersonAcselProps): Promise<void> => {
  updateMultipleInputs({
    names: '',
    lastNames: '',
    birthday: '',
    gender: '',
    civilStatus: '',
    birthPlace: '',
    addressCountry: '',
    addressState: '',
    addressDetail: '',
    addressCity: '',
    addressTownship: '',
    originType: '',
    enterprise: '',
    originCountry: '',
    salary: '',
    typeOfMoney: moneyTypes[0].Value,
    phoneNumber: '',
    email: '',
    occupation: '',
  });

  if (!person) return;

  const {
    names,
    lastnames,
    birthday,
    gender,
    maritalStatus,
    email,
    stateDescription,
    address,
    districtDescription,
    townshipDescription,
    phoneGroup,
  } = person;

  getInputProps('names').onChange(names || dataFields.names);
  getInputProps('lastNames').onChange(lastnames || dataFields.lastNames);

  const movilPhone = phoneGroup?.find((item) => item.type === 'MOVIL');
  const selectedPhone = movilPhone || phoneGroup?.[0];

  getInputProps('phoneNumber').onChange(
    selectedPhone?.number.replace(/-/g, '') || dataFields.phoneNumber,
  );
  getInputProps('birthday').onChange(
    birthday ? getAcselDate(birthday) : dataFields.birthday,
  );
  getInputProps('gender').onChange(gender || dataFields.gender);
  const civilStatusId = civilStatus.find(
    (item) => item.code.toLowerCase() === maritalStatus?.toLowerCase(),
  )?.code;
  getInputProps('civilStatus').onChange(civilStatusId);
  getInputProps('email').onChange(email || dataFields.email);

  const state = states.find(
    (item) => item.name.toLowerCase() === stateDescription?.toLowerCase(),
  );

  const newState = state?.code ? `${COSTA_RICA}-${state.code}` : '';

  getInputProps('birthPlace').onChange(newState);
  getInputProps('addressCountry').onChange(COSTA_RICA);
  getInputProps('addressState').onChange(state?.code);
  getInputProps('addressDetail').onChange(address);
  setNewDataFieldsAction({
    names: names || dataFields.names,
    lastNames: lastnames,
    birthday,
    gender,
  });
  setCityOptions(getCatalogToOptions(cities, state?.id));
  const city = getCatalog(cities, districtDescription, state?.id);
  getInputProps('addressCity').onChange(city?.code);
  setTownshipOptions(getCatalogToOptions(townships, city?.id));

  const township = getCatalog(townships, townshipDescription, city?.id)?.code;
  getInputProps('addressTownship').onChange(township);

  if (person.sourceFund) {
    const {
      countrySourceFund,
      country,
      companyName,
      catalogCurrency,
      catalogSourceFundRange,
      catalogActivityId,
    } = person.sourceFund;
    const { catalogDescription } = catalogSourceFundRange;
    const { catalogId } = catalogCurrency;

    getInputProps('originType').onChange(countrySourceFund);
    getInputProps('originCountry').onChange(country);
    const typeOfMoney =
      catalogId === '$' ? moneyTypes[1].Value : moneyTypes[0].Value;

    const salary = catalogDescription.split('A')[1];
    const salaryFormatted = salary.slice(0, -1);
    getInputProps('salary').onChange(
      typeOfMoney === 'CRC' ? getAcselSalary(salaryFormatted) : salaryFormatted,
    );
    getInputProps('typeOfMoney').onChange(typeOfMoney);
    getInputProps('enterprise').onChange(companyName);
    getInputProps('occupation').onChange(catalogActivityId);
  }
};

export const updateToInitialValues = ({
  updateMultipleInputs,
  getInputProps,
  dataFields,
}: {
  updateMultipleInputs: (newInputData: {
    [key: string]: FormValueTypes;
  }) => void;
  getInputProps: (inputName: string) => FormInputProps;
  dataFields: EmissionDataFields;
}): void => {
  updateMultipleInputs({
    legalEntityName: '',
    socialReason: '',
    phoneNumber: '',
    email: '',
    birthday: '',
    birthPlace: '',
    addressCountry: '',
    addressState: '',
    addressDetail: '',
    addressCity: '',
    addressTownship: '',
    economicActivity: '',
  });

  getInputProps('legalEntityName').onChange(dataFields.legalEntityName);
  getInputProps('socialReason').onChange(dataFields.socialReason);
  getInputProps('phoneNumber').onChange(dataFields.phoneNumber);
  getInputProps('email').onChange(dataFields.email);
  getInputProps('birthday').onChange(dataFields.birthday);
  getInputProps('birthPlace').onChange(dataFields.birthPlace);
  getInputProps('addressCountry').onChange(dataFields.addressCountry);
  getInputProps('addressState').onChange(dataFields.addressState);
  getInputProps('addressDetail').onChange(dataFields.addressDetail);
  getInputProps('addressCity').onChange(dataFields.addressCity);
  getInputProps('addressTownship').onChange(dataFields.addressTownship);
  getInputProps('economicActivity').onChange(dataFields.economicActivity);
};

export const addLegalPersonACSEL = async ({
  legalPerson,
  dataFields,
  getInputProps,
  setNewDataFields,
  setCityOptions,
  setTownshipOptions,
}: AddLegalPersonAcselProps): Promise<void> => {
  if (!legalPerson) return;

  const {
    birthday,
    names,
    email,
    stateDescription,
    address,
    districtDescription,
    townshipDescription,
    phoneGroup,
    profession,
    country,
  } = legalPerson;

  getInputProps('legalEntityName').onChange(names || dataFields.names);
  getInputProps('socialReason').onChange(names || dataFields.names);

  const movilPhone = phoneGroup.find((item) => item.type === 'MOVIL');
  const selectedPhone = movilPhone || phoneGroup[0];

  getInputProps('phoneNumber').onChange(
    selectedPhone.number.replace(/-/g, '') || dataFields.phoneNumber,
  );
  getInputProps('email').onChange(email || dataFields.email);

  getInputProps('birthday').onChange(
    birthday ? getAcselDate(birthday) : dataFields.birthday,
  );

  const state = states.find(
    (item) => item.name.toLowerCase() === stateDescription.toLowerCase(),
  );

  getInputProps('birthPlace').onChange(country);

  getInputProps('addressCountry').onChange(COSTA_RICA);
  getInputProps('addressState').onChange(state?.code);
  getInputProps('addressDetail').onChange(address);
  setNewDataFields({
    legalEntityName: names || dataFields.names,
  });
  setCityOptions(getCatalogToOptions(cities, state?.id));
  const city = getCatalog(cities, districtDescription, state?.id);
  getInputProps('addressCity').onChange(city?.code);
  setTownshipOptions(getCatalogToOptions(townships, city?.id));

  const township = getCatalog(townships, townshipDescription, city?.id)?.code;
  getInputProps('addressTownship').onChange(township);

  getInputProps('economicActivity').onChange(profession);
};

export const addLegalPersonEquifax = async ({
  person,
  getInputProps,
  prefixKey,
  setBeneficiaryCityOptions,
  setBeneficiaryTownshipOptions,
}: AddLegalPersonEquifaxProps): Promise<void> => {
  if (!person.data.personas_estudio_completo) return;
  const {
    nombre,
    apellido1,
    apellido2,
    nacimiento,
    provincia,
    canton,
    distrito_electoral: district,
    desc_distrito_administrativo: districtName,
  } = person.data.personas_estudio_completo.datos_filiacion;
  const { direcciones } = person.data.personas_estudio_completo;
  getInputProps(`${prefixKey}FirstName`).onChange(nombre || '');
  const lastName = (
    apellido1 || apellido2 ? `${apellido1} ${apellido2}` : ''
  ).trim();
  getInputProps(`${prefixKey}LastName`).onChange(lastName);
  const birthday = nacimiento ? getEquifaxDate(nacimiento) : '';
  getInputProps(`${prefixKey}Birthdate`).onChange(birthday);
  getInputProps(`${prefixKey}BirthPlaceId`).onChange(COSTA_RICA);
  getInputProps(`${prefixKey}CountryId`).onChange(COSTA_RICA);
  const state = getCatalog(states, provincia);
  getInputProps(`${prefixKey}StateId`).onChange(state?.code);
  const newAddress = [...direcciones];
  const address =
    newAddress.length > 0
      ? newAddress.sort(
          (a, b) =>
            new Date(getEquifaxDate(b.fecha)).getTime() -
            new Date(getEquifaxDate(a.fecha)).getTime(),
        )[0].direccion
      : '';
  getInputProps(`${prefixKey}AddressDetail`).onChange(address);

  setBeneficiaryCityOptions(getCatalogToOptions(cities, state?.id));
  const city = getCatalog(
    cities,
    canton.toLowerCase() === 'central' ? provincia : canton,
    state?.id,
  );
  getInputProps(`${prefixKey}CityId`).onChange(city?.code);
  setBeneficiaryTownshipOptions(getCatalogToOptions(townships, city?.id));
  const township =
    getCatalog(townships, district, city?.id)?.code ??
    getCatalog(townships, districtName, city?.id)?.code;
  getInputProps(`${prefixKey}TownshipId`).onChange(township);
};

export const payerNaturalPersonEquifax = async ({
  person,
  dataFields,
  getInputProps,
  setNewPayerDataFieldsAction,
}: PayerPersonEquifaxProps): Promise<void> => {
  if (!person.data.personas_estudio_completo) return;
  const { nombre, apellido1, apellido2, nacimiento, genero } =
    person.data.personas_estudio_completo.datos_filiacion;
  getInputProps('names').onChange(nombre || dataFields.names);
  const lastName = (
    apellido1 || apellido2 ? `${apellido1} ${apellido2}` : dataFields.lastNames
  ).trim();
  getInputProps('lastNames').onChange(lastName);
  const birthday = nacimiento
    ? getEquifaxDate(nacimiento)
    : dataFields.birthday;
  getInputProps('birthday').onChange(birthday);
  const gender = genero ? getGenderEquifax(genero) : dataFields.gender;
  getInputProps('gender').onChange(gender);
  setNewPayerDataFieldsAction({
    names: nombre || dataFields.names,
    lastNames: lastName,
    birthday,
    gender,
  });
};

export const addShareholderEquifax = async ({
  index,
  person,
  getInputProps,
}: ShareholderEquifaxProps): Promise<void> => {
  if (!person.data.personas_estudio_completo) return;
  const { nombre, apellido1, apellido2, nacimiento, genero } =
    person.data.personas_estudio_completo.datos_filiacion;
  const { estado_civil: status } =
    person.data.personas_estudio_completo.datos_estado_civil;
  getInputProps(`share${index}firstName`).onChange(nombre || '');
  const lastName = (
    apellido1 || apellido2 ? `${apellido1} ${apellido2}` : ''
  ).trim();
  getInputProps(`share${index}lastName`).onChange(lastName);
  const gender = genero ? getGenderEquifax(genero) : '';
  getInputProps(`share${index}gender`).onChange(gender);
  const birthday = nacimiento ? getEquifaxDate(nacimiento) : '';
  getInputProps(`share${index}birthday`).onChange(birthday);
  getInputProps(`share${index}nationality`).onChange(COSTA_RICA);
  const civilStatusId = getCivilStatusIdByCode(status);
  getInputProps(`share${index}civilStatus`).onChange(civilStatusId);
};
